import type { BooleanFilterConstraints } from '../filter-components/boolean-operators';
import type { DateTimeFilterConstraints } from '../filter-components/datetime-operators';
import type { LinkFilterConstraints } from '../filter-components/link-operators';
import type { NumberFilterConstraints } from '../filter-components/number-operators';
import type { SelectFilterConstraints } from '../filter-components/select-operators';
import type { StringFilterConstraints } from '../filter-components/string-operators';
import { FilterExtension, StandardFilterTypes } from '../filters/group';
import { DataSourceExtension } from '../filters/types';

export const COLUMN_INPUT_WIDTH = '9rem';

export type FilterExtensionProps<T extends string | string[] = string | string[], TDataSourceArgs = unknown> = {
	setValue: (value: T) => void;
	value?: T;
	filterParams?: unknown; // stringified JSON filterparams (i.e. min or max value you can select)
	dataSource?: DataSourceExtension<TDataSourceArgs>;
};

export type FieldTypeMap = Record<string, StandardFilterTypes>;
export type FieldOperatorMap<T extends FieldTypeMap> = {
	[K in keyof T]: T[K] extends 'select'
		? SelectFilterConstraints
		: T[K] extends 'datetime'
			? DateTimeFilterConstraints
			: T[K] extends 'date'
				? DateTimeFilterConstraints
				: T[K] extends 'number'
					? NumberFilterConstraints
					: T[K] extends 'boolean'
						? BooleanFilterConstraints
						: T[K] extends 'text'
							? StringFilterConstraints
							: T[K] extends 'link'
								? LinkFilterConstraints
								: never;
};

export type NestedDefaultExtension<T = string> = T extends FieldTypeMap
	? {
			[K in keyof T]: {
				field: K;
				operator: FieldOperatorMap<T>[K];
				value?: string;
				values?: string[];
			};
		}[keyof T][]
	: { field: string; operator: string; value?: string; values?: string[] }[];

export type FilterExtensionWithField<K extends keyof FieldTypeMap> = Omit<FilterExtension, 'field' | 'type'> & {
	field: K;
	type: FieldTypeMap[K];
};

export type ListFiltersType = Array<
	{
		[K in keyof FieldTypeMap]: FilterExtensionWithField<K>;
	}[keyof FieldTypeMap]
>;

export type SelectMeta = {
	colourCoded: boolean;
	options?: { label: string; id: string; colour?: string }[];
	allowMulti: boolean;
};

export type TreeSelectData = {
	label: string;
	selectable: boolean;
	value: string;
	children: TreeSelectData[];
};
export type TreeSelectMeta = {
	allowMulti: boolean;
	data: TreeSelectData[];
};

type Link = {
	id: string;
	type: string;
};

export type LinkFilterMeta = {
	entity: Link;
	extraDetails: string;
	image: string;
	name: string;
};
